import React, { useMemo, useCallback, useRef } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import styled from '~/app/components/styled'
import { Typography } from '@yanolja-rnd/ya-com-core'
import { isAvailableDomesticLeisureOption } from '~/app/components/Cart/CartOrderHelper'
import {
  ItemContainer,
  DescContainer,
  PriceContainer,
  WarningNotice,
  OptionDescriptionContainer,
  OptionNameContainer,
} from '~/app/components/Cart/CartItem/CartItem.styles'
import { Toast, ToastHandler } from '@yanolja-rnd/ya-com-toast'
import Stepper from '~/app/components/common/Stepper/Stepper'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import { toKRW } from '~/app/utils/krNumberToString'
import getMaxQuantityOfLeisureItem from '~/app/utils/getMaxQuantityOfLeisureItem'
import LeisureCancelPolicy from '~/app/components/common/Leisure/LeisureCancelPolicy'
import yaLogo from '~/app/imgs/empty-thumnail-yanolja-h-56.svg?inline'
import { ConfigBadgeWrapper } from '~/app/components/common/Badge/Badge.styles'
import ConfigBadgeGroup from '~/app/components/common/Badge/ConfigBadgeGroup'
import { Icon_close, Icon_info } from '@yanolja-rnd/ya-com-system-icon'
import Checkbox from '~/app/components/common/Checkbox/YaComCheckbox'
import { getBadgeTypeWithProductEvent, getPreOrderLeisureValidityPeriod } from '~/api/utils/leisure'

/**
 * DomesticLeisureItem component
 * author: jaegeunlee
 * description
 */
const PeriodContainer = styled.div<{ disabled: boolean }>`
  margin-top: 8px;
  margin-bottom: 6px;
  color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray600)};
  ${Typography('body2').regular};
`

const MemberOnlyTextContainer = styled.div`
  margin-top: 1.2rem;
  ${Typography('body2').regular};
  color: #e31068;
`

const StepperWrapper = styled.div`
  flex: 0 0 10rem;
  margin-left: 28px;
`

export type LeisureOption = Extract<
  CartItemsV3Query['cartItemsV3'][number]['options'][number],
  { __typename?: 'DomesticLeisureCartItemOption' }
>

export type LeisureProduct = LeisureOption['product']

export interface DomesticLeisureItemProps {
  currentAt: string
  option: LeisureOption
  product: LeisureProduct
  onCheck?: (checked: boolean, id: string) => void
  onChange?: (quantity: number, id: string) => void
  onClose?: (id: string) => void
  onTitleClick?: (id: string) => void
  onCancelRefreshButtonClick?: () => void
}

const DomesticLeisureItem: React.FC<DomesticLeisureItemProps> = ({
  currentAt,
  option,
  product,
  onCheck,
  onChange,
  onClose,
  onTitleClick,
}) => {
  const toastRef = useRef<ToastHandler>()

  const {
    id,
    isChosen,
    quantity,
    price,
    availability,
    remains = null,
    outdated,
    unavailableReason,
    variant: {
      variantName,
      validityStartDateTime,
      validityEndDateTime,
      productOptionGroup: { productOptionGroupName },
      cancellationFeePlans,
    },
  } = option

  const stock = getMaxQuantityOfLeisureItem(option)
  const isDisabled = useMemo(() => !isAvailableDomesticLeisureOption(option), [option])
  const {
    productImage: { thumbnailImageUrl: imageUrl = yaLogo },
    isMemberOnly,
    productInfo,
    productEvent,
  } = product
  const isStepperDisabled = useMemo(() => {
    return isDisabled && option.unavailableReason !== 'OUT_OF_STOCK'
  }, [unavailableReason])
  const stockAlmostExhausted = useMemo(() => !isDisabled && stock < 5, [isDisabled, remains])

  const setQuantity = useCallback(
    (quantity: number) => {
      /**
       * 장바구니에 담긴 수량이 10 개이고 남은 재고가 3개일 경우
       * 4 개 이상은 업데이트가 불가능 하므로 - 한번클릭에 남은 재고의 수인 3까지 내리도록 한다
       */
      if (unavailableReason === 'OUT_OF_STOCK') {
        onChange?.(stock, id)
      } else {
        onChange?.(quantity, id)
      }
    },
    [onChange, stock, id]
  )

  const triggerToast = useCallback(message => {
    toastRef.current?.addMessage({ message, timeout: 3000 })
  }, [])

  const onDisabledMinClick = useCallback(
    (count: number) => {
      if (isStepperDisabled) {
        return
      }
      triggerToast(`${count}개 이상부터 예약하실 수 있습니다`)
    },
    [isStepperDisabled]
  )
  const onDisabledMaxClick = useCallback(
    (count: number) => {
      if (isStepperDisabled) {
        return
      }
      triggerToast(`이 상품은 최대 ${count}개까지 구매할 수 있어요`)
    },
    [isStepperDisabled]
  )

  const statusText = useMemo(() => {
    // 판매 완료, 판매 종로, 예약 불가
    if (!availability) {
      if (option.unavailableReason === 'STOP') {
        return '판매 종료'
      } else if (option.unavailableReason === 'SOLD_OUT') {
        return '판매 완료'
      } else if (option.unavailableReason === 'OUT_OF_STOCK') {
        return '수량 부족'
      }
      return '예약 불가'
    } else if (outdated) {
      return '예약 불가'
    }
    return ''
  }, [availability, remains, outdated])

  const leisureValidityPeriodText = useMemo(
    () =>
      getPreOrderLeisureValidityPeriod({
        validityStartDateTime,
        validityEndDateTime,
        currentAt,
      }),
    [option, currentAt]
  )

  const badges = useMemo(() => {
    return getBadgeTypeWithProductEvent({
      productEvent,
      currentAt,
    })
  }, [currentAt, productEvent])

  return (
    <>
      <OptionNameContainer disabled={isDisabled} onClick={() => onTitleClick?.(option.id)}>
        {variantName}
      </OptionNameContainer>
      <ItemContainer>
        <Checkbox
          className="check-icon-wrapper"
          onClick={() => onCheck?.(isDisabled ? false : !isChosen, id)}
          checked={isChosen}
          disabled={isDisabled}
        />
        <div className="image-wrapper fix-height">
          <LazyLoadImage src={imageUrl} width={72} height={72} alt="티켓 이미지" />
        </div>
        <DescContainer>
          {!!badges.length && (
            <ConfigBadgeWrapper>
              <ConfigBadgeGroup badges={badges} />
            </ConfigBadgeWrapper>
          )}
          <OptionDescriptionContainer disabled={isDisabled}>
            {productOptionGroupName || ''}
          </OptionDescriptionContainer>
          <PeriodContainer disabled={isDisabled}>{leisureValidityPeriodText}</PeriodContainer>
          {isMemberOnly && <MemberOnlyTextContainer>로그인 회원 전용 상품</MemberOnlyTextContainer>}
          {outdated && (
            <WarningNotice>
              <Icon_info size={1.6} unit="rem" />
              <span>상품 정보가 변경되었습니다. 상품을 다시 선택해주세요.</span>
            </WarningNotice>
          )}
        </DescContainer>
        <div className="icon-wrapper" onClick={() => onClose?.(id)}>
          <Icon_close size={1.6} unit="rem" />
        </div>
      </ItemContainer>
      <PriceContainer disabled={isDisabled}>
        <div>
          <StepperWrapper>
            <Stepper
              value={quantity}
              onChange={setQuantity}
              min={1}
              max={stock}
              disabledMinClick={onDisabledMinClick}
              disabledMaxClick={onDisabledMaxClick}
              disabled={isStepperDisabled}
            />
          </StepperWrapper>
        </div>
        <div className="price-wrapper">
          <div>
            <span className="price">{toKRW(price * quantity)}</span>
          </div>
          {!isDisabled && (
            <div className="cancel-policy-wrapper">
              <LeisureCancelPolicy
                cancellationFeePlans={cancellationFeePlans}
                refundInfo={productInfo?.refundInfo}
              />
            </div>
          )}
          {stockAlmostExhausted && <div className="almost-exhausted">품절임박</div>}
          {statusText && <div className="status-text">{statusText}</div>}
        </div>
        <Toast toastRef={toastRef} />
      </PriceContainer>
    </>
  )
}

export default DomesticLeisureItem
